import { EMPTY_GENERAL_REPORT_ENTITY, GeneralReportEntity } from "domain/entity/GeneralReport/GeneralReportEntity";
import { MasterDataType } from "domain/entity/MasterData/MasterDataEntity";
import { ChargeTypeRepository } from "domain/repository/ChargeType/ChargeTypeRepo";
import { PrintQueueRepository } from "domain/repository/Common/PrintQueue";
import { RemoteFileInfoRepository } from "domain/repository/Common/RemoteFileInfoRepo";
import { CompanyRepository } from "domain/repository/Company/CompanyRepo";
import { CustomerRepository } from "domain/repository/Company/CustomerRepo";
import { CriteriaTypeRepository } from "domain/repository/Criteria/CriteriaTypeRepo";
import { GeneralReportRepository } from "domain/repository/GeneralReport/GeneralReportRepo";
import { MasterDataRepository } from "domain/repository/MasterData/MasterDataRepo";
import { OpsLineRepository } from "domain/repository/OpsLine/OpsLineRepo";
import { ParameterDetailRepository } from "domain/repository/parameter/ParameterDetailRepo";
import { StandardProposalRepository } from "domain/repository/StandardProposal/StandardProposalRepo";
import { StandardTariffCodeRepository } from "domain/repository/TariffCode/StandardTariffCodeRepo";
import { TariffTypeRepository } from "domain/repository/TariffCode/TariffTypeRepo";
import { UserRepository } from "domain/repository/User/UserRepo";
import _ from "lodash";
import moment from "moment";
import { AllNbisivPermission } from "presentation/constant/ANAInfo/NbisivPermission";
import { generatePermissionByRptIdWithBU, ReportView } from "presentation/constant/ANAInfo/ReportPermission";
import { CompanyCodeDroOpts } from "presentation/constant/DropDownOptions/Company/CompanyCodeDroOpts";
import { CompanyTypeDroOpts } from "presentation/constant/DropDownOptions/Report/CompanyTypeDroOpts";
import { exceptionTypeDropdownOption } from "presentation/constant/DropDownOptions/StaticDropdownOptions";
import { DropdownProps } from "presentation/model/DropdownProps";
import { GeneralReportModel } from "presentation/model/GeneralReport/GeneralReportModel";
import { generalReportEnabledField } from "presentation/utils/report/ReportEnabledFieldUtils";
import { Dispatch, SetStateAction } from "react";
import { GroupCheckboxList, IFieldValue } from "veronica-ui-component/dist/component/core";
import BaseViewModel from "../BaseViewModel";

interface GeneralReportVMProps extends BaseViewModel {
    dispatch: [
        Dispatch<SetStateAction<GeneralReportModel>> | ((value: SetStateAction<GeneralReportModel>) => void),
    ],
    generalReportRepo: GeneralReportRepository,
    printQueueRepo: PrintQueueRepository,
    remoteFileInfoRepo: RemoteFileInfoRepository,
    companyRepo: CompanyRepository,
    customerRepo: CustomerRepository,
    criteriaTypeRepo: CriteriaTypeRepository,
    chargeTypeRepo: ChargeTypeRepository,
    tariffTypeRepo: TariffTypeRepository,
    masterDataRepo: MasterDataRepository,
    standardProposalRepo: StandardProposalRepository,
    parameterDetailRepo: ParameterDetailRepository,
    opsLineRepo: OpsLineRepository,
    userRepo: UserRepository,
    standardTariffCodeRepo: StandardTariffCodeRepository,
}

export const GeneralReportVM = ({ dispatch, generalReportRepo, printQueueRepo, remoteFileInfoRepo, companyRepo, customerRepo,
    criteriaTypeRepo, chargeTypeRepo, tariffTypeRepo, masterDataRepo, standardProposalRepo, parameterDetailRepo,
    opsLineRepo, userRepo, standardTariffCodeRepo }: GeneralReportVMProps) => {
    const [generalReportDispatch] = dispatch;

    const loadDropdownOption = async () => {
        const [tariffNatures, terminals, serviceCodes, tariffTypes, chargeTypes,
            customers, printQueueList, fileLocs, companies, criteriaTypes, opsLines,
            userList, tariffCodeList, stdProposals] = await Promise.all([
                masterDataRepo.getMasterDataByKey(MasterDataType.TAR_NATURE),
                masterDataRepo.getMasterDataByKey(MasterDataType.TERMINAL),
                masterDataRepo.getMasterDataByKey(MasterDataType.SERVICE),
                tariffTypeRepo.getAllActiveTariffTypes(),
                chargeTypeRepo.getAllChargeTypesForCombobox(),
                customerRepo.getAllCustomerForCombobox(),
                printQueueRepo.getAllPrintQueues(),
                remoteFileInfoRepo.getFileLoc(),
                companyRepo.getAllCompanyForCombobox(),
                criteriaTypeRepo.getAllCriteriaType(),
                opsLineRepo.getOpsLineForCombobox(),
                userRepo.getAllUsers(),
                standardTariffCodeRepo.getAllStandardTariffCodes(),
                standardProposalRepo.searchStandardProposal({ statusList: ["FINALIZED"] })
            ]);

        let tariffNatureDropdownOptions = tariffNatures?.map((entity) => ({
            dropdownLabel: entity.code,
            tagLabel: entity.code,
            value: entity.code,
        })) ?? []

        let operatingTmlDropdownOptions = terminals?.map((entity) => ({
            dropdownLabel: entity.code,
            tagLabel: entity.code,
            value: entity.code,
        })) ?? []

        const serviceCodeDropdownOptions = serviceCodes?.map((serviceCode) => ({
            dropdownLabel: serviceCode.code,
            tagLabel: serviceCode.code,
            value: serviceCode.code,
        })) ?? []

        let newTariffs = _.orderBy(tariffTypes, ["tariffType"]);
        let tariffTypeDropdownOptions = newTariffs?.map((entity) => ({
            dropdownLabel: entity.tariffType,
            tagLabel: entity.tariffType,
            value: entity.tariffType,
        })) ?? []

        let newChargeTypes = _.orderBy(chargeTypes, ["chargeType", "subChargeType"]);
        let chargeTypeDropdownOption: DropdownProps[] = [];
        newChargeTypes?.forEach(chgTypeEty => {
            const chgTypeExisted = chargeTypeDropdownOption?.find(chgType =>
                chgType.value === chgTypeEty.chargeType);
            if (!chgTypeExisted) {
                chargeTypeDropdownOption.push({
                    dropdownLabel: chgTypeEty.chargeType,
                    tagLabel: chgTypeEty.chargeType,
                    value: chgTypeEty.chargeType
                })
            }
        });

        const sortCustomers = _.orderBy(customers, ["customerCode"]);
        let customerCodeDropdownOptions: DropdownProps[] = [];
        for (let i = 0; i < sortCustomers.length; i++) {
            if (sortCustomers[i].id) {
                customerCodeDropdownOptions.push({
                    dropdownLabel: sortCustomers[i].customerCode,
                    tagLabel: sortCustomers[i].id.toString(),
                    value: sortCustomers[i].customerCode
                });
            }
        }

        let printQueues = printQueueList.filter(entity => entity.activeInd === 'Y');

        const dataObj = printQueues.reduce((acc, item) => {
            const key = item.id;
            const value = item.printQueueName;
            acc[value] = key;
            return acc;
        }, {} as { [key: string]: number });


        let printQueueDropdownOptions = printQueues?.map((printQueue) => ({
            dropdownLabel: printQueue.printQueueName,
            tagLabel: printQueue.printQueueName,
            value: printQueue.printQueueName,
        })) ?? []
        printQueueDropdownOptions = _.orderBy(printQueueDropdownOptions, "dropdownLabel");

        let fileLocationDropdownOptions = fileLocs?.map((fileLoc) => ({
            dropdownLabel: fileLoc,
            tagLabel: fileLoc,
            value: fileLoc,
        })) ?? []
        fileLocationDropdownOptions = _.orderBy(fileLocationDropdownOptions, "dropdownLabel");

        const droOptObj = CompanyCodeDroOpts();
        const allCompanies = droOptObj.getAllCompanyModel(companies);
        const masterCompanies = droOptObj.getMasterCompanyModel(companies);
        const nonMasterAlliances = droOptObj.getNonMasterAllianceModel(_.orderBy(companies, ["companyCode"]));
        const ccCompanies = droOptObj.getCcCompanyModel(companies);
        const slCompanies = droOptObj.getSlCompanyModel(companies)

        let companyNameDropdownOptions = companies?.filter(company =>
            (company.companyType === 'ALLIANCE' || (company.companyType !== 'ALLIANCE' && company.status === 'CONFIRMED'))
            && company.customerType !== 'SL' && company.activeInd === 'Y'
        )
            .map((companyName) => ({
                dropdownLabel: companyName.companyName,
                tagLabel: companyName.companyName,
                value: companyName.companyName,
            })) ?? []

        const types: string[] = criteriaTypes?.map(item => {
            const [key] = item.split(':', 2);
            return key;
        });
        const criteriaTypeIdMap = criteriaTypes.reduce((acc, item) => {
            const [key, value] = item.split(':', 2);
            acc[key] = value.trim();
            return acc;
        }, {} as { [key: string]: string });
        let criteriaTypeDropdownOption = types?.map((type) => ({
            dropdownLabel: type,
            tagLabel: type,
            value: type,
        })) ?? []

        let shipmenttypeList: string[] = ["GP", "FR", "OT", "PF", "RF", "DG"];
        let subTypeList: string[] = ["LPA", "COD"];
        let thcList: string[] = ["Load", "Discharge"];
        let cntrSzieList: string[] = ["20", "40", "45"];
        let cntrStatusList: string[] = ["Empty", "Full", "Transshipment"];

        const shipmentTypeDropdownOption = shipmenttypeList?.map((shipmentType) => ({
            dropdownLabel: shipmentType,
            tagLabel: shipmentType,
            value: shipmentType,
        })) ?? []

        const subTypeDropdownOption = subTypeList?.map((subType) => ({
            dropdownLabel: subType,
            tagLabel: subType,
            value: subType,
        })) ?? []
        const thcDropdownOption = thcList?.map((thc) => ({
            dropdownLabel: thc,
            tagLabel: thc,
            value: thc,
        })) ?? []
        const cntrSizeDropdownOption = cntrSzieList?.map((size) => ({
            dropdownLabel: size,
            tagLabel: size,
            value: size,
        })) ?? []
        const cntrStatusDropdownOption = cntrStatusList?.map((status) => ({
            dropdownLabel: status,
            tagLabel: status,
            value: status,
        })) ?? []

        let opsLineDropdownOption: DropdownProps[] = [];
        opsLines?.forEach((opsLineEty) => {
            const opsExisted = opsLineDropdownOption?.find(opsLine =>
                opsLine.value === opsLineEty.opsLine);
            if (!opsExisted) {
                opsLineDropdownOption.push({
                    dropdownLabel: opsLineEty.opsLine,
                    tagLabel: opsLineEty.opsLine,
                    value: opsLineEty.opsLine
                })
            }
        })

        const userDropdownOption = userList?.map((user) => ({
            dropdownLabel: user.name,
            tagLabel: user.name,
            value: user.name,
        })) ?? []

        const tariffCodeDropdownOption = tariffCodeList?.map((tariff) => ({
            dropdownLabel: tariff.tariffCode,
            tagLabel: tariff.tariffCode,
            value: tariff.tariffCode,
        })) ?? []

        let newStdProposals = _.orderBy(stdProposals, ["companyCode", "proposalNo"]);
        let proposalDropdownOptions: { [key: string]: DropdownProps[] } = {};
        newStdProposals?.forEach(stdProposal => {
            if (stdProposal.companyCode && stdProposal.proposalNo) {
                if (!proposalDropdownOptions[stdProposal.companyCode]) {
                    proposalDropdownOptions[stdProposal.companyCode] = [];
                }
                proposalDropdownOptions[stdProposal.companyCode].push({
                    dropdownLabel: stdProposal.proposalNo,
                    tagLabel: stdProposal.proposalNo,
                    value: stdProposal.proposalNo
                });
            }
        });

        generalReportDispatch(prevState => ({
            ...prevState,
            printQueueIdMap: dataObj,
            criteriaTypeIdMap: criteriaTypeIdMap,
            dynamicOptions: {
                ...prevState.dynamicOptions,
                operatingTmlDropdownOptions: operatingTmlDropdownOptions,
                tariffNatureDropdownOptions: tariffNatureDropdownOptions,
                serviceCodeDropdownOptions: serviceCodeDropdownOptions,
                tariffTypeDropdownOptions: tariffTypeDropdownOptions,
                chargeTypeDropdownOptions: chargeTypeDropdownOption,
                customerCodeDropdownOptions: customerCodeDropdownOptions,
                printQueueDropdownOptions: printQueueDropdownOptions,
                fileLocationDropdownOptions: fileLocationDropdownOptions,
                allCompanyCodeDropdownOptions: allCompanies,
                masterCompanyCodeDropdownOptions: masterCompanies,
                companyCodeDropdownOptions: nonMasterAlliances,
                companyNameDropdownOptions: companyNameDropdownOptions,
                ccCompanyCodeDropdownOptions: ccCompanies,
                slCompanyCodeDropdownOptions: slCompanies,
                criteriaTypeDropdownOptions: [...criteriaTypeDropdownOption],
                opsLineDropdownOptions: opsLineDropdownOption,
                cashierDropdownOptions: userDropdownOption,
                tariffCodeDropdownOptions: tariffCodeDropdownOption,
                proposalNoDropdownOptions: proposalDropdownOptions,
                thcDropdownOptions: thcDropdownOption,
                containerSizeDropdownOptions: cntrSizeDropdownOption,
                containerStatusDropdownOptions: cntrStatusDropdownOption,
                exceptionTypeDropdownOptions: exceptionTypeDropdownOption,
                subTypeDropdownOptions: subTypeDropdownOption,
                shipmentTypeDropdownOptions: shipmentTypeDropdownOption
            },
            currentReport: {
                ...prevState.currentReport,
                printer: printQueueDropdownOptions.length > 0 ? printQueueDropdownOptions[0].value : '',
                fileLocation: fileLocationDropdownOptions.length > 0 ? fileLocationDropdownOptions[0].value : '',
                exceptionType: exceptionTypeDropdownOption.length > 0 ? exceptionTypeDropdownOption[0].value : '',
            },
            enabledField: {
                ...prevState.enabledField,
                companyCode: false,
                billingType: false,
                docNoFrom: true,
                docNoTo: true,
                docDateFrom: true,
                docDateTo: true,
                transactionDateFrom: false,
                transactionDateTo: false,
                proposalNo: false,
                adjustmentDateFrom: false,
                adjustmentDateTo: false
            }
        }))
    }

    const initGeneralReportList = async (permissions: AllNbisivPermission, operatingUnit: string) => {
        await generalReportRepo.initGeneralReportList().then(data => {

            const [reportIds, reportNames] = data.reduce<[string[], string[]]>((acc, item) => {
                const [key, value] = item.split(':', 2);
                const rptPermission = generatePermissionByRptIdWithBU(ReportView.GENERAL_REPORT, key, operatingUnit)
                const permission = permissions[rptPermission];

                if (permission && permission.acl && permission.acl.includes('R')) {
                    acc[0].push(key);
                    acc[1].push(value.trim());
                }
                return acc;
            }, [[], []] as [string[], string[]]);

            const dataObj = data.reduce((acc, item) => {
                const [key, value] = item.split(':', 2);
                acc[value] = key.trim();
                return acc;
            }, {} as { [key: string]: string });

            const reportTypeDropdownOption = reportNames?.map((report) => ({
                dropdownLabel: report,
                tagLabel: report,
                value: report,
            })) ?? []
            generalReportDispatch(prevState => {
                return {
                    ...prevState,
                    reportIdTypeMap: dataObj,
                    reportTypeList: reportNames,
                    reportIdList: reportIds,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        reportTypeDropdownOptions: reportTypeDropdownOption
                    },
                    currentReport: {
                        ...prevState.currentReport,
                        reportType: reportTypeDropdownOption.length > 0 ? reportTypeDropdownOption[0].value : '',
                        version: "Normal",
                        proposalType: "Both",
                        exceptionType: "CHARGE_GEN",
                        month: "01",
                    }
                }
            })
        })

    }

    const onHeaderFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;

        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }

        generalReportDispatch(prevState => {
            if (fieldKey === 'amount' || fieldKey === 'depositAmount') {
                var amountIntReg = /^\d*$/;
                if (!amountIntReg.test(val)) {
                    val = prevState.currentReport[fieldKey];
                }
            }

            if (fieldKey === 'companyCodeFrom') {
                prevState = onCompanyFromDropChanged(val, prevState);
            } else if (fieldKey === 'companyCodeTo') {
                prevState = onCompanyToDropChanged(val, prevState);
            }

            return {
                ...prevState,
                currentReport: {
                    ...prevState.currentReport,
                    [fieldKey]: val,
                },
            }
        })
    }

    const onFieldChangeUpperCase = (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;

        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value?.toUpperCase() || item.key?.toUpperCase()));
        } else {
            val = val?.toUpperCase();
        }

        generalReportDispatch(prevState => {
            return {
                ...prevState,
                currentReport: {
                    ...prevState.currentReport,
                    [fieldKey]: val
                }
            }
        })
    }

    const onMultipleDropdownChange = (e: any, fieldName: string) => {
        generalReportDispatch(prevState => {
            return {
                ...prevState,
                currentReport: {
                    ...prevState.currentReport,
                    [fieldName]: _.uniq(e?.map((item: DropdownProps) => item.value)),
                }
            }
        });
    };

    const onGroupCheckboxChange = (e: (GroupCheckboxList | undefined)[], currentReport: GeneralReportEntity, fieldName: string) => {

        let selectedValue: string[] = [];
        if (e) {
            e?.forEach(function (value, index) {
                if (value) {
                    selectedValue.push(value.key);
                }
            });
        }
        //currentReport = { ...currentReport, [fieldName]: selectedValue }
        generalReportDispatch(prevState => ({
            ...prevState,
            currentReport: {
                ...prevState.currentReport,
                [fieldName]: selectedValue
            }
        }));
    };

    const onReportTypeChanged = (fieldKey: string, fieldValue: IFieldValue, operatingUnit: string) => {
        let reportType: any = fieldValue;
        const enabledField = generalReportEnabledField(reportType, operatingUnit);
        const firstDay = moment().startOf('month').toDate();
        const lastDay = moment().endOf('month').toDate();
        const yesterday = moment().subtract(1, 'days').startOf('day').toDate();
        const today = moment().startOf('day').toDate();

        generalReportDispatch(prevState => {
            prevState.enabledField = enabledField;
            prevState.currentReport = {
                ...EMPTY_GENERAL_REPORT_ENTITY,
                [fieldKey]: reportType,
                printer: prevState.dynamicOptions.printQueueDropdownOptions.length > 0 ? prevState.dynamicOptions.printQueueDropdownOptions[0].value : '',
                fileLocation: prevState.dynamicOptions.fileLocationDropdownOptions.length > 0 ? prevState.dynamicOptions.fileLocationDropdownOptions[0].value : '',
                companyCode: "Let Pass Account Monthly Statement" === reportType ? prevState.dynamicOptions.companyCodeDropdownOptions.length > 0 ? prevState.dynamicOptions.companyCodeDropdownOptions[0].value : '' : '',
                exceptionType: prevState.dynamicOptions.exceptionTypeDropdownOptions.length > 0 ? prevState.dynamicOptions.exceptionTypeDropdownOptions[0].value : '',
                proposalType: 'Both',
                version: 'Normal',
                dateFrom: "Let Pass Account Monthly Statement" === reportType ? firstDay : ("Invoice Summary Report" === reportType ? yesterday : null),
                dateTo: "Let Pass Account Monthly Statement" === reportType ? lastDay : ("Invoice Summary Report" === reportType ? today : null),
            };
            //prevState.allFormState = {};

            prevState = onCompanyTypeAndTypeForReportIdChanged(reportType, operatingUnit, prevState);

            return { ...prevState }
        })
        /*generalReportDispatch(prevState => {
            return {
                ...prevState,
                enabledField: enabledField,
                currentReport: {
                    ...prevState.currentReport,
                    reportType: reportType,
                    printer: prevState.dynamicOptions.printQueueDropdownOptions.length > 0 ? prevState.dynamicOptions.printQueueDropdownOptions[0].value : '',
                    fileLocation: prevState.dynamicOptions.fileLocationDropdownOptions.length > 0 ? prevState.dynamicOptions.fileLocationDropdownOptions[0].value : '',
                    masterCompanyCode: '',
                    companyCode: "Let Pass Account Monthly Statement" === reportType ? prevState.dynamicOptions.companyCodeDropdownOptions.length > 0 ? prevState.dynamicOptions.companyCodeDropdownOptions[0].value : '' : '',
                    customerCode: '',
                    activeInd: '',
                    criteriaType: '',
                    criteriaName: '',
                    docNoFrom: null,
                    docNoTo: null,
                    docDateFrom: null,
                    docDateTo: null,
                    opsDateFrom: null,
                    opsDateTo: null,
                    proposalNo: null,
                    effectiveDateFrom: null,
                    effectiveDateTo: null,
                    expiryDateFrom: null,
                    expiryDateTo: null,
                    glDateFrom: null,
                    glDateTo: null,
                    chargeOnCompany: '',
                    yearInput: '',
                    month: '',
                    exceptionDateFrom: null,
                    exceptionDateTo: null,
                    exceptionType: prevState.dynamicOptions.exceptionTypeDropdownOptions.length > 0 ? prevState.dynamicOptions.exceptionTypeDropdownOptions[0].value : '',
                    ediView: '',
                    chargeType: '',
                    date: null,
                    proposalType: 'Both',
                    proposalStatus: '',
                    version: 'Normal',
                    companyCodeFrom: '',
                    companyCodeTo: '',
                    proposalFrom: '',
                    proposalTo: '',
                    tariffNature: '',
                    tariffType: '',
                    exportType: null,
                    //sapt/kict
                    vesselArrivalFromDate: null,
                    vesselArrivalToDate: null,
                    thcList: [],
                    shipmentTypeList: [],
                    containerSizeList: [],
                    containerStatusList: [],
                    type: null,
                    opsLineFrom: null,
                    opsLineTo: null,
                    consortiumCode: null,
                    vessleCode: null,
                    voyageCode: null,
                    billToCompany: null,
                    companyType: null,
                    companyNameList: [],
                    ntnNo: null,
                    amount: null,
                    transactionType: null,
                    transactionFromDate: null,
                    transactionTomDate: null,
                    depositAmount: null,
                    userId: null,
                    shipperConsignee: null,
                    shipperConsigneeCompanyName: null,
                    subType: null,
                    docType: null,
                    viewList: [],
                    masterChargeOnCompany: null,
                    operatingTml: null,
                    serviceCode: null,
                    referenceFromDate: null,
                    referenceTomDate: null,
                    approvedFromDate: null,
                    approvedTomDate: null,
                    dateFrom: "Let Pass Account Monthly Statement" === reportType ? firstDay : ("Invoice Summary Report" === reportType ? yesterday : null),
                    dateTo: "Let Pass Account Monthly Statement" === reportType ? lastDay : ("Invoice Summary Report" === reportType ? today : null),
                    opsDate: null,
                    lpaCompanyCodeList: [],
                    //AQCT/OICT/RSCT
                    settlementFromDate: null,
                    settlementToDate: null,
                    collFromDate: null,
                    collToDate: null,
                    cashierList: [],
                    shippingLineList: [],
                    tariffCodeList: [],
                    opsLine: null,
                    standardView: "Y",
                    creditView: null,
                    adjustmentView: null
                }
            }
        })*/
    }

    const onResetClick = (operatingUnit: string) => {
        return generalReportDispatch(prevState => {
            const reportType = prevState.dynamicOptions.reportTypeDropdownOptions.length > 0 ? prevState.dynamicOptions.reportTypeDropdownOptions[0].value : ''
            const enabledField = generalReportEnabledField(reportType, operatingUnit);

            prevState.enabledField = enabledField;
            prevState.currentReport = {
                ...EMPTY_GENERAL_REPORT_ENTITY,
                reportType: reportType,
                printer: prevState.dynamicOptions.printQueueDropdownOptions.length > 0 ? prevState.dynamicOptions.printQueueDropdownOptions[0].value : '',
                fileLocation: prevState.dynamicOptions.fileLocationDropdownOptions.length > 0 ? prevState.dynamicOptions.fileLocationDropdownOptions[0].value : '',
                exceptionType: prevState.dynamicOptions.exceptionTypeDropdownOptions.length > 0 ? prevState.dynamicOptions.exceptionTypeDropdownOptions[0].value : '',
                proposalType: 'Both',
                standardView: "Y",
            };

            return { ...prevState }
        })

        /*generalReportDispatch(prevState => ({
            ...prevState,
            currentReport: {
                ...prevState.currentReport,
                reportType: prevState.dynamicOptions.reportTypeDropdownOptions.length > 0 ? prevState.dynamicOptions.reportTypeDropdownOptions[0].value : '',
                printer: prevState.dynamicOptions.printQueueDropdownOptions.length > 0 ? prevState.dynamicOptions.printQueueDropdownOptions[0].value : '',
                fileLocation: prevState.dynamicOptions.fileLocationDropdownOptions.length > 0 ? prevState.dynamicOptions.fileLocationDropdownOptions[0].value : '',
                masterCompanyCode: '',
                companyCode: '',
                customerCode: '',
                activeInd: '',
                criteriaType: '',
                criteriaName: '',
                docNoFrom: null,
                docNoTo: null,
                docDateFrom: null,
                docDateTo: null,
                opsDateFrom: null,
                opsDateTo: null,
                proposalNo: null,
                effectiveDateFrom: null,
                effectiveDateTo: null,
                expiryDateFrom: null,
                expiryDateTo: null,
                glDateFrom: null,
                glDateTo: null,
                chargeOnCompany: '',
                year: '',
                month: '',
                exceptionDateFrom: null,
                exceptionDateTo: null,
                exceptionType: prevState.dynamicOptions.exceptionTypeDropdownOptions.length > 0 ? prevState.dynamicOptions.exceptionTypeDropdownOptions[0].value : '',
                ediView: '',
                chargeType: '',
                date: null,
                proposalType: 'Both',
                proposalStatus: '',
                version: 'Normal',
                companyCodeFrom: '',
                companyCodeTo: '',
                proposalFrom: '',
                proposalTo: '',
                tariffNature: '',
                tariffType: '',
                exportType: null,
                //sapt/kict
                vesselArrivalFromDate: null,
                vesselArrivalToDate: null,
                thcList: [],
                shipmentTypeList: [],
                containerSizeList: [],
                containerStatusList: [],
                type: null,
                opsLineFrom: null,
                opsLineTo: null,
                consortiumCode: null,
                vessleCode: null,
                voyageCode: null,
                billToCompany: null,
                companyType: null,
                companyNameList: [],
                ntnNo: null,
                amount: null,
                transactionType: null,
                transactionFromDate: null,
                transactionTomDate: null,
                depositAmount: null,
                userId: null,
                shipperConsignee: null,
                shipperConsigneeCompanyName: null,
                subType: null,
                docType: null,
                viewList: [],
                masterChargeOnCompany: null,
                operatingTml: null,
                serviceCode: null,
                referenceFromDate: null,
                referenceTomDate: null,
                approvedFromDate: null,
                approvedTomDate: null,
                dateFrom: null,
                dateTo: null,
                opsDate: null,
                lpaCompanyCodeList: [],
                //AQCT/OICT/RSCT
                settlementFromDate: null,
                settlementToDate: null,
                collFromDate: null,
                collToDate: null,
                cashierList: [],
                shippingLineList: [],
                tariffCodeList: [],
                opsLine: null,
                standardView: "Y",
                creditView: null,
                adjustmentView: null
            }
        }))*/
    }

    const onPrintClick = async (reportIdTypeMap: { [key: string]: string }, entity: GeneralReportEntity) => {
        let data = { ...entity };
        data = {
            ...data,
            reportId: reportIdTypeMap[entity.reportType],
        }
        return await generalReportRepo.print(data);
    }


    const onViewClick = async (reportIdTypeMap: { [key: string]: string }, entity: GeneralReportEntity) => {
        let data = { ...entity };
        data = {
            ...data,
            reportId: reportIdTypeMap[entity.reportType],
        }

        return await generalReportRepo.view(data).then((data) => {
            if (data && data.success) {
                const res = data.data;
                if (_.isEmpty(res)) {
                    return "false";
                }
                for (const key in res) {
                    if (res[key]) {
                        const tempData = res[key] as string;
                        let base64 = tempData;
                        let bstr = atob(base64);
                        let n = bstr.length;
                        let u8arr = new Uint8Array(n);
                        while (n--) {
                            u8arr[n] = bstr.charCodeAt(n)
                        }
                        const blob = new Blob([u8arr], { type: 'application/pdf' });
                        const url = URL.createObjectURL(blob);
                        window.open(url, '_blank');
                        //URL.revokeObjectURL(url);
                    }
                }
                return "true";
            }
            return "false";
        })
    }


    const onExportClick = async (exportType: string, reportIdTypeMap: { [key: string]: string }, entity: GeneralReportEntity) => {
        let data = { ...entity };
        data = {
            ...data,
            reportId: reportIdTypeMap[entity.reportType],
            exportType: exportType,
        }
        return await generalReportRepo.exportReport(data);
    }

    const onDownloadClick = async (reportIdTypeMap: { [key: string]: string }, entity: GeneralReportEntity, downloadType: string,) => {
        let data = { ...entity };
        data = {
            ...data,
            reportId: reportIdTypeMap[entity.reportType],
            exportType: downloadType,
        }
        return await generalReportRepo.downloadReport(data).then((data) => {
            if (data && data.success) {
                const res = data.data;
                for (const key in res) {
                    if (res[key]) {
                        const tempData = res[key] as string;
                        let base64 = tempData;
                        let bstr = atob(base64);
                        let n = bstr.length;
                        let u8arr = new Uint8Array(n);
                        while (n--) {
                            u8arr[n] = bstr.charCodeAt(n)
                        }
                        // const blob = new Blob([u8arr], { type: 'application/pdf' });
                        // const blob = downloadType === "CSV" ? new Blob([u8arr], { type: 'text/csv' }) : new Blob([u8arr], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                        const blob = downloadType === "CSV" ? new Blob([u8arr], { type: 'text/csv' }) : (downloadType === "PDF" ? new Blob([u8arr], { type: 'application/pdf' }) : new Blob([u8arr], { type: 'application/vnd.ms-excel' }));
                        const url = URL.createObjectURL(blob);
                        if (downloadType) {
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = key + "." + downloadType.toLowerCase();
                            a.style.display = 'none';
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                        }
                    }
                }
                return "success";
            } else {
                return data.data ?? "Download Failed.";

            }
        })
    }

    const onCheckboxChange = (checked: boolean, fieldName: string) => {
        generalReportDispatch(prevState => {
            return {
                ...prevState,
                currentReport: {
                    ...prevState.currentReport,
                    [fieldName]: checked ? "Y" : "N",
                },
            }
        })
    }

    const onCriteriaTypeChanged = async (criteriaId: string) => {
        await criteriaTypeRepo.getAllCriteriaName(Number(criteriaId)).then((data) => {
            let criteriaNameDropdownOption = data?.map((name) => ({
                dropdownLabel: name,
                tagLabel: name,
                value: name,
            })) ?? []
            criteriaNameDropdownOption = _.orderBy(criteriaNameDropdownOption, "dropdownLabel");

            return generalReportDispatch(prevState => ({
                ...prevState,
                dynamicOptions: {
                    ...prevState.dynamicOptions,
                    criteriaNameDropdownOptions: [
                        ...criteriaNameDropdownOption],
                },
                currentReport: {
                    ...prevState.currentReport,
                    criteriaName: "",
                }
            }))
        })
    }

    const onRemoveCompanyFrom = async () => {
        generalReportDispatch(prevState => ({
            ...prevState,
            dynamicOptions: {
                ...prevState.dynamicOptions,
                proposalNoFromDropdownOptions: [],
            },
            currentReport: {
                ...prevState.currentReport,
                proposalFrom: "",
            }
        }))
    }
    const onCompanyFromDropChanged = (companyFrom: string, prevState: GeneralReportModel) => {
        const propsalNoDroOpts = prevState.dynamicOptions.proposalNoDropdownOptions[companyFrom];
        prevState.currentReport.proposalFrom = propsalNoDroOpts && propsalNoDroOpts.length > 0 ? propsalNoDroOpts[0].value : '';

        return prevState;
        /*generalReportDispatch(prevState => {
            const propsalNoDroOpts = prevState.dynamicOptions.proposalNoDropdownOptions[companyFrom];
    
            return {
                ...prevState,
                currentReport: {
                    ...prevState.currentReport,
                    proposalFrom: propsalNoDroOpts && propsalNoDroOpts.length > 0 ? propsalNoDroOpts[0].value : ''
                }
            };
        });*/
    }
    const onRemoveCompanyTo = async () => {
        generalReportDispatch(prevState => ({
            ...prevState,
            dynamicOptions: {
                ...prevState.dynamicOptions,
                proposalNoToDropdownOptions: [],
            },
            currentReport: {
                ...prevState.currentReport,
                proposalTo: "",
            }
        }))
    }
    const onCompanyToDropChanged = (companyTo: string, prevState: GeneralReportModel) => {
        const propsalNoDroOpts = prevState.dynamicOptions.proposalNoDropdownOptions[companyTo];
        prevState.currentReport.proposalTo = propsalNoDroOpts && propsalNoDroOpts.length > 0 ? propsalNoDroOpts[0].value : '';

        return prevState;
        /*generalReportDispatch(prevState => {
            const propsalNoDroOpts = prevState.dynamicOptions.proposalNoDropdownOptions[companyTo];
            
            return {
                ...prevState,
                currentReport: {
                    ...prevState.currentReport,
                    proposalTo: propsalNoDroOpts && propsalNoDroOpts.length > 0 ? propsalNoDroOpts[0].value : ''
                }
            };
        });*/
    }

    const onCompanyTypeAndTypeForReportIdChanged = (reportType: string, operatingUnit: string, prevState: GeneralReportModel) => {
        const droOptObj = CompanyTypeDroOpts(operatingUnit);

        const compTypeDroOpts_1 = droOptObj.getDroOptsModel_1();
        const compTypeDroOpts_2 = droOptObj.getDroOptsModel_2();
        const compTypeDroOpts_3 = droOptObj.getDroOptsModel_3();

        if (reportType === "Credit Notes Summary Report") {
            prevState.dynamicOptions.typeDropdownOptions = compTypeDroOpts_1;
        }
        if (reportType === "Invoice Summary Report" || reportType === "Receival and Delivery Charges Summary") {
            prevState.dynamicOptions.companyTypeDropdownOptions = compTypeDroOpts_1;
        }

        if (reportType === "Sales Tax Report") {
            prevState.dynamicOptions.companyTypeDropdownOptions = compTypeDroOpts_2;

            const defaultVal = compTypeDroOpts_2 && compTypeDroOpts_2.length > 0 ? compTypeDroOpts_2[0].value : "";
            prevState.currentReport.companyType = defaultVal;
        }

        if (reportType === "CC Invoice/Credit Note Summary Report") {
            prevState.dynamicOptions.typeDropdownOptions = compTypeDroOpts_3;

            const defaultVal = compTypeDroOpts_3 && compTypeDroOpts_3.length > 0 ? compTypeDroOpts_3[0].value : "";
            prevState.currentReport.type = defaultVal;
        }

        return prevState;
    }

    const onRadioChange = (e: any, fieldName: string) => {
        let val = "";
        if (e.checked) {
            val = "Y";
        }

        /*return generalReportDispatch(prevState => ({
            ...prevState,
    
            currentReport: {
                ...prevState.currentReport,
                [fieldName]: val
            }
        }))*/
        return generalReportDispatch(prevState => {
            return {
                ...prevState,
                currentReport: {
                    ...prevState.currentReport,
                    [fieldName]: val
                }
            };
        });
    };

    const onSearchFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }
        generalReportDispatch(prevState => {

            return {
                ...prevState,
                currentReport: {
                    ...prevState.currentReport,
                    [fieldKey]: val,
                },
            }
        })
    }

    return {
        onSearchFieldChange: onSearchFieldChange,
        onCompanyToDropChanged: onCompanyToDropChanged,
        onCompanyFromDropChanged: onCompanyFromDropChanged,
        onRemoveCompanyTo: onRemoveCompanyTo,
        onRemoveCompanyFrom: onRemoveCompanyFrom,
        onCriteriaTypeChanged: onCriteriaTypeChanged,
        onPrintClick: onPrintClick,
        onExportClick: onExportClick,
        onViewClick: onViewClick,
        onResetClick: onResetClick,
        onReportTypeChanged: onReportTypeChanged,
        onHeaderFieldChange: onHeaderFieldChange,
        onFieldChangeUpperCase: onFieldChangeUpperCase,
        loadDropdownOption: loadDropdownOption,
        onRadioChange: onRadioChange,
        initGeneralReportList: initGeneralReportList,
        onCheckboxChange: onCheckboxChange,
        onGroupCheckboxChange: onGroupCheckboxChange,
        onMultipleDropdownChange: onMultipleDropdownChange,
        //onCompanyTypeAndTypeForReportIdChanged: onCompanyTypeAndTypeForReportIdChanged,
        onDownloadClick: onDownloadClick,
    }
}